import { useCallback, useEffect, useState } from "react";
import { registerSW } from "virtual:pwa-register";

const CHECK_INTERVAL_MS = 30 * 60 * 1000;

type UpdateSW = (reloadPage?: boolean) => Promise<void>;

let updateSW: UpdateSW | undefined;
let registered = false;
let needRefreshValue = false;
const listeners = new Set<(needRefresh: boolean) => void>();

function notify(needRefresh: boolean) {
  needRefreshValue = needRefresh;
  for (const listener of listeners) {
    listener(needRefresh);
  }
}

function ensureRegistered() {
  if (registered || typeof window === "undefined") {
    return;
  }
  registered = true;

  updateSW = registerSW({
    immediate: true,
    onNeedRefresh() {
      notify(true);
    },
    onRegisteredSW(_swUrl, registration) {
      if (!registration) {
        return;
      }

      const check = () => {
        void registration.update();
      };

      window.setInterval(check, CHECK_INTERVAL_MS);

      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") {
          check();
        }
      });

      window.addEventListener("focus", check);
    },
  });
}

export function usePwaUpdate() {
  const [needRefresh, setNeedRefresh] = useState(needRefreshValue);

  useEffect(() => {
    ensureRegistered();
    listeners.add(setNeedRefresh);
    setNeedRefresh(needRefreshValue);
    return () => {
      listeners.delete(setNeedRefresh);
    };
  }, []);

  const applyUpdate = useCallback(() => {
    notify(false);
    void updateSW?.(true);
  }, []);

  const dismiss = useCallback(() => {
    notify(false);
  }, []);

  return { needRefresh, applyUpdate, dismiss };
}
